import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, formatCurrency } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import LanguageSelector from '../components/LanguageSelector';
import { 
  Users, Wallet, Calendar, Loader2, 
  CheckCircle, AlertCircle, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../i18n';

export const JoinPage = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);

  const getTypeLabel = (type) => t(`tontine.types.${type}`);
  const getFrequencyLabel = (frequency) => t(`tontine.frequencies.${frequency}`);

  useEffect(() => {
    loadInviteInfo();
  }, [inviteCode]);

  const loadInviteInfo = async () => {
    try {
      const response = await api.getInviteInfo(inviteCode);
      setInfo(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || t('joinPage.invalidOrExpired'));
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!user) {
      // Redirect to auth with return URL
      navigate(`/auth?redirect=/join/${inviteCode}`);
      return;
    }

    setJoining(true);
    try {
      const response = await api.joinByInvite(inviteCode);
      toast.success(t('joinPage.welcomeToast', { tontineName: response.data.tontine_name }));
      navigate(`/tontines/${response.data.tontine_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('joinPage.joinError'));
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="font-display text-xl font-bold text-stone-900 mb-2">
              {t('joinPage.invalidTitle')}
            </h2>
            <p className="text-stone-500 mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline">{t('joinPage.backHome')}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center p-4 pt-16 sm:pt-4">
      <div className="fixed top-2 right-2 sm:top-4 sm:right-4 z-10">
        <LanguageSelector />
      </div>
      <Card className="max-w-md w-full card-neo" data-testid="join-page">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="font-display text-2xl">
            {t('joinPage.title')}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Tontine Info */}
          <div className="text-center">
            <h3 className="font-display text-xl font-bold text-stone-900 dark:text-stone-50 mb-1">
              {info.tontine_name}
            </h3>
            {info.description && (
              <p className="text-stone-500 text-sm">{info.description}</p>
            )}
            <Badge variant="outline" className="mt-2">
              {getTypeLabel(info.type)}
            </Badge>
          </div>

          {/* Details */}
          <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4 space-y-3">
            <div className="flex items-start sm:items-center justify-between gap-3">
              <span className="text-stone-500 flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                {t('tontine.contribution')}
              </span>
              <span className="font-semibold text-stone-900 dark:text-stone-50 text-right">
                {formatCurrency(info.contribution_amount, info.currency)}
              </span>
            </div>
            <div className="flex items-start sm:items-center justify-between gap-3">
              <span className="text-stone-500 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t('tontine.frequency')}
              </span>
              <span className="text-stone-700 dark:text-stone-300 text-right">
                {getFrequencyLabel(info.frequency)}
              </span>
            </div>
            <div className="flex items-start sm:items-center justify-between gap-3">
              <span className="text-stone-500 flex items-center gap-2">
                <Users className="w-4 h-4" />
                {t('tontine.members')}
              </span>
              <span className="text-stone-700 dark:text-stone-300 text-right">
                {info.current_members}/{info.max_members}
              </span>
            </div>
          </div>

          {/* Status */}
          {info.is_full ? (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 text-center">
              <AlertCircle className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
              <p className="text-yellow-700 dark:text-yellow-400">
                {t('joinPage.fullTontine')}
              </p>
            </div>
          ) : (
            <Button 
              className="w-full btn-accent text-lg py-6"
              onClick={handleJoin}
              disabled={joining}
              data-testid="join-btn"
            >
              {joining ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-5 h-5 mr-2" />
              )}
              {user ? t('joinPage.joinButton') : t('joinPage.loginToJoin')}
            </Button>
          )}

          {!user && (
            <p className="text-center text-sm text-stone-500">
              {t('joinPage.needAccount')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinPage;
